import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { has_access } from "../../../middleware/privileges";
import { GET_STOCK_COUNTS, GET_BRANCHES } from "./queries";
import {
  StockCount,
  COUNT_TYPE_LABELS,
  COUNT_STATUS_LABELS,
} from "./models";
import InventoryNav from "./inventoryNav";

const btn = {
  txt: lang("Nuevo Conteo", "New Count"),
  icon: "fa fa-plus",
  link: "/inventory/counts/new",
};

const StockCountList: React.FC = () => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  const [fetchCounts, { data, loading }] = useLazyQuery(GET_STOCK_COUNTS, {
    fetchPolicy: "no-cache",
  });
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchBranches();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchCounts({
      variables: {
        status: statusFilter || undefined,
        branch_id: branchFilter ? parseInt(branchFilter) : undefined,
        take: 50,
      },
    });
    //eslint-disable-next-line
  }, [statusFilter, branchFilter]);

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "—";
    try {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <>
      <HeaderTitle
        title={lang("Conteos de Inventario", "Inventory Counts")}
        btn={has_access(256) ? btn : null}
      />
      <InventoryNav active="counts" />

      <div className="row mb-3">
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{lang("Todos los estados", "All statuses")}</option>
            {Object.entries(COUNT_STATUS_LABELS).map(([key, val]) => (
              <option key={key} value={key}>
                {lang(val.label, val.labelEn)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">{lang("Todas las sucursales", "All branches")}</option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {data && (
        <div className="row">
          <div className="col-lg-12">
            <div className="material_card p-3 table-responsive">
              <table className="table table-hover vertical_middle">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>{lang("Tipo", "Type")}</th>
                    <th>{lang("Sucursal", "Branch")}</th>
                    <th>{lang("Estado", "Status")}</th>
                    <th>{lang("Inicio", "Started")}</th>
                    <th>{lang("Fin", "Completed")}</th>
                    <th className="text-center">{lang("Items", "Items")}</th>
                    <th className="text-right">
                      {lang("Costo Varianza", "Variance Cost")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.getStockCounts.map((count: StockCount) => {
                    const typeInfo = COUNT_TYPE_LABELS[count.count_type] || {
                      label: count.count_type,
                      labelEn: count.count_type,
                    };
                    const statusInfo = COUNT_STATUS_LABELS[count.status] || {
                      label: count.status,
                      labelEn: count.status,
                      color: "secondary",
                    };

                    return (
                      <tr
                        key={count.id}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          count.status === "in_progress"
                            ? history.push(`/inventory/counts/new?resume=${count.id}`)
                            : history.push(`/inventory/counts/${count.id}`)
                        }
                      >
                        <td className="font-weight-bold">#{count.id}</td>
                        <td>{lang(typeInfo.label, typeInfo.labelEn)}</td>
                        <td>{count.branch?.name || "—"}</td>
                        <td>
                          <span className={`badge badge-${statusInfo.color}`}>
                            {lang(statusInfo.label, statusInfo.labelEn)}
                          </span>
                        </td>
                        <td>{formatDate(count.started_at)}</td>
                        <td>{formatDate(count.completed_at)}</td>
                        <td className="text-center">{count.total_items}</td>
                        <td className="text-right">
                          {count.total_variance_cost != null
                            ? (
                                <span
                                  className={
                                    count.total_variance_cost < 0
                                      ? "text-danger font-weight-bold"
                                      : count.total_variance_cost > 0
                                      ? "text-warning font-weight-bold"
                                      : ""
                                  }
                                >
                                  ${Number(count.total_variance_cost).toFixed(2)}
                                </span>
                              )
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {data.getStockCounts.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        {lang(
                          "No hay conteos registrados",
                          "No counts registered"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StockCountList;
